// Analytics utility for GA4 and custom event tracking
// For demo: gracefully degrades if GA4 is not configured

import type { WindowWithGtag, GtagFunction, AnalyticsEvent, AnalyticsParams } from '@/types/analytics';

export type { AnalyticsEvent, AnalyticsParams };

export const trackEvent = (
  eventName: AnalyticsEvent,
  params?: AnalyticsParams
) => {
  // Check if gtag is available
  if (typeof window !== "undefined" && (window as WindowWithGtag).gtag) {
    (window as WindowWithGtag).gtag!("event", eventName, params);
  }

  // Fallback console log for demo/debugging
  if (import.meta.env.DEV) {
    console.log("📊 Analytics Event:", eventName, params);
  }
};

export const trackPageView = (path: string) => {
  if (typeof window !== "undefined" && (window as WindowWithGtag).gtag) {
    (window as WindowWithGtag).gtag!(
      "config",
      import.meta.env.VITE_GA_ID || "G-XXXXXXXXXX",
      {
        page_path: path,
      }
    );
  }
};

export const initAnalytics = () => {
  const gaId = import.meta.env.VITE_GA_ID;
  const env = import.meta.env.VITE_ENVIRONMENT || "development";
  const isDev = import.meta.env.DEV;

  if (!gaId) {
    if (isDev) {
      console.log(
        `⚠️  GA4 not configured for ${env} environment. Set VITE_GA_ID in .env.${env}`
      );
    }
    return;
  }

  if (isDev) {
    console.log(`📊 Analytics initialized in ${env} mode with ID: ${gaId}`);
  }

  // Load GA4 script
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  script.async = true;
  document.head.appendChild(script);

  const windowWithGtag = window as WindowWithGtag;
  windowWithGtag.dataLayer = windowWithGtag.dataLayer || [];
  windowWithGtag.gtag = function(...args: Parameters<GtagFunction>) {
    windowWithGtag.dataLayer!.push(args);
  } as GtagFunction;

  windowWithGtag.gtag("js", new Date());
  windowWithGtag.gtag("config", gaId, {
    debug_mode: isDev || env === "staging",
  });
};
